import { colors } from '@apollo/space-kit/colors';
import { identity } from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React, { useCallback, useEffect, useMemo } from 'react';

import { parseCoordinateFromString } from 'src/app/graph/checksPage/checkWorkflowTaskDetailsPage/proposalCheckDetails/proposalCheckHelpers';
import { getEditorLineNumberFromParsedCoordinate } from 'src/app/graph/documentationPage/schemaPage/sdlEditorHelpers';
import { useDerivedEditorValue } from 'src/app/graph/explorerPage/hooks/useDerivedEditorValue';
import { useEditorModel } from 'src/app/graph/explorerPage/hooks/useEditorModel';
import {
  DOCUMENT_FORMATTING_EDIT_PROVIDER,
  DOCUMENT_RANGE_FORMATTING_EDIT_PROVIDER,
} from 'src/app/graph/explorerPage/operationsPane/graphqlEditor/GraphqlEditor';
import { proposalEditorRouteConfig } from 'src/app/graph/routes';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { StudioEditorContainer } from 'src/hooks/useStudioEditor';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { getDiffLineNumbers } from '../../../diffHelpers/getDiffLineNumbers';
import { splitOutTargetsFromCoordinate } from '../../../proposalChanges/DetailDiff';
import { useDebouncedDocumentNode } from '../../hooks/useDebouncedDocumentNode';
import { ProposalSchemaInfo } from '../../hooks/useProposalEditorState/useProposalEditorState';

import { EditorCommentButtons } from './editorCommentButtons/EditorCommentButtons';
import { LineDiffMarkers } from './lineDiffMarkers/LineDiffMarkers';

// this is an arbitrary number, we know a customer had a 3_418_496 character long sdl that hangs the page
// we _may_ need to tweak this down if a custoer with a 500_001 character long sdl has issues, but that schema is hard to come by
// update: coinbase's 998_041 character long sdl also hangs the page
const STOP_SHOWING_CHANGED_LINES_THRESHOLD = 500_000;

export const ProposalSDLEditor = ({
  editor,
  selectedSchemaInfo,
  parentComments,
  selectedSubgraphHasChanges,
}: {
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  selectedSchemaInfo: ProposalSchemaInfo;
  parentComments: GraphQLTypes.ProposalEditorQuery_service_variant_proposal_parentComments_ParentChangeProposalComment[];
  selectedSubgraphHasChanges: boolean;
}) => {
  const { linkedCoordinateName: selectedCoordinate } = useRouteParams(
    proposalEditorRouteConfig,
  );

  const editorTextValue = useDerivedEditorValue({
    editor,
    getValue: identity,
    withPosition: false,
  });

  const stableGetChangedLines = useCallback(
    (editorValue: string) => {
      const readOnlyLinesChanged = {
        added: [],
        removed: [],
        modified: [],
      };

      return (
        // diffing the sdl is a lot of computation.
        // skip the diffing if the editor is readonly since it will always have 0 changes
        editor.getOption(monacoEditor.editor.EditorOption.readOnly) === true ||
          // or if we already know there are no changes in the subgraphs
          !selectedSubgraphHasChanges ||
          // or if the text is too long and will cause the page to hang
          // TODO(Evan): switch to a faster (wasm?) diffing library? possibly https://github.com/google/diff-match-patch/wiki/Line-or-Word-Diffs#line-mode
          Math.max(
            editorValue.length,
            selectedSchemaInfo?.initialText?.length ?? 0,
          ) > STOP_SHOWING_CHANGED_LINES_THRESHOLD
          ? readOnlyLinesChanged
          : getDiffLineNumbers(
              selectedSchemaInfo?.initialText ?? '',
              editorValue,
            )
      );
    },
    [editor, selectedSchemaInfo?.initialText, selectedSubgraphHasChanges],
  );

  const {
    added: addedLines,
    removed: removedLines,
    modified: modifiedLines,
  } = useDerivedEditorValue({
    editor,
    getValue: stableGetChangedLines,
    withPosition: false,
  });

  const debouncedDocumentNode = useDebouncedDocumentNode(editor);

  // TODO: for every parent comment, get the corresponding line number and group them by line number
  // TODO: then we should implement the designs for multi-line threads to allow users to open up threads on the same
  // TODO: line.
  // TODO: if the coordinate linked does not exist in the list of parent comments and does not exist in the document node
  // TODO: remove it from the url

  const commentsByLineNumber = useMemo(() => {
    const commentsInCurrentScope = parentComments.filter(
      (comment) => comment.schemaScope === selectedSchemaInfo.id,
    );

    return commentsInCurrentScope.reduce(
      (
        items: Record<number, Array<(typeof commentsInCurrentScope)[number]>>,
        currentItem,
      ) => {
        const { coordinate: schemaCoordinate, target } =
          splitOutTargetsFromCoordinate(currentItem.schemaCoordinate);

        const coordinate = parseCoordinateFromString(schemaCoordinate ?? '');
        const lineNumber = getEditorLineNumberFromParsedCoordinate(
          debouncedDocumentNode,
          coordinate,
          target === 'description',
          target === 'returnType',
        );
        if (!lineNumber) return items;

        if (!items[lineNumber]) {
          return {
            ...items,
            [lineNumber]: [currentItem],
          };
        }
        items[lineNumber]?.push(currentItem);
        return items;
      },
      {},
    );
  }, [debouncedDocumentNode, parentComments, selectedSchemaInfo.id]);

  const { coordinate: selectedCoordinateWithoutTargets, target } = useMemo(
    () => splitOutTargetsFromCoordinate(selectedCoordinate),
    [selectedCoordinate],
  );

  let lineNumberToScrollTo: number | undefined;
  let maybeEndLineNumberToHighlight: number | undefined;
  if (selectedCoordinateWithoutTargets) {
    const coordinate = parseCoordinateFromString(
      selectedCoordinateWithoutTargets,
    );
    const lineNumber = getEditorLineNumberFromParsedCoordinate(
      debouncedDocumentNode,
      coordinate,
      target === 'description',
      target === 'returnType',
    );

    const trueCoordinateLineNumber = getEditorLineNumberFromParsedCoordinate(
      debouncedDocumentNode,
      coordinate,
    );

    if (target === 'description' && trueCoordinateLineNumber) {
      maybeEndLineNumberToHighlight = trueCoordinateLineNumber - 1;
    }

    if (lineNumber && commentsByLineNumber[lineNumber]) {
      lineNumberToScrollTo = lineNumber;
    }
  }

  const allDeltaDecorations = useMemo(() => {
    const monacoDeltaDecorations: monacoEditor.editor.IModelDeltaDecoration[] =
      [];
    if (lineNumberToScrollTo) {
      monacoDeltaDecorations.push({
        range: new monacoEditor.Range(
          lineNumberToScrollTo,
          1,
          maybeEndLineNumberToHighlight ?? lineNumberToScrollTo,
          1,
        ),
        options: {
          isWholeLine: true,
          className: 'xxx-monaco-highlight-line',
          linesDecorationsClassName: 'xxx-monaco-highlight-line',
          marginClassName: 'xxx-monaco-highlight-line',
        },
      });
    }

    return {
      modelDecorations: [...(monacoDeltaDecorations ?? [])],
    };
  }, [lineNumberToScrollTo, maybeEndLineNumberToHighlight]);

  useEffect(() => {
    const monacoDisposables = [
      monacoEditor.languages.registerDocumentFormattingEditProvider(
        'graphql',
        DOCUMENT_FORMATTING_EDIT_PROVIDER,
      ),
      monacoEditor.languages.registerDocumentRangeFormattingEditProvider(
        'graphql',
        DOCUMENT_RANGE_FORMATTING_EDIT_PROVIDER,
      ),
    ];
    return () => {
      monacoDisposables.forEach((disposable) => disposable.dispose());
    };
  });

  const model = useEditorModel(editor);

  useEffect(() => {
    if (!model) return;

    const decorations = editor.createDecorationsCollection(
      allDeltaDecorations.modelDecorations,
    );

    // Clean up decorations
    return () => {
      decorations.clear();
    };
    // this needs to happen on mount and when text is loaded,
    // so update whenever the editor text changes
  }, [
    editor,
    allDeltaDecorations,
    lineNumberToScrollTo,
    model,
    editorTextValue,
  ]);

  useEffect(() => {
    if (lineNumberToScrollTo) {
      editor.revealLineInCenterIfOutsideViewport(lineNumberToScrollTo);
    }
  }, [editor, lineNumberToScrollTo]);

  return (
    <>
      <style>
        {`
          .xxx-monaco-highlight-line {
            background-color: ${colors.yellow.lighter};
            border-top: 1px dashed ${colors.yellow.base};
            border-bottom: 1px dashed ${colors.yellow.base};
            z-index: -1;
          }

          /* TODO (jason) fix this upstream */
          /* The monaco glyph hover css is broken, add some size/padding */
          .monaco-editor-hover
            .hover-row
            > div:not(.hover-contents):not(.actions) {
            font-size: 12px;
            padding-top: 4px;
            padding-bottom: 4px;
            padding-left: 8px;
            padding-right: 8px;
            font-family: 'var(--font-mono)';
          }
          .monaco-editor-hover .hover-row .hover-contents p {
            padding-top: 8px;
          }
        `}
      </style>
      <LineDiffMarkers
        editor={editor}
        changedLines={addedLines}
        type="added"
        editorTextValue={editorTextValue}
      />
      <LineDiffMarkers
        editor={editor}
        changedLines={modifiedLines}
        type="modified"
        editorTextValue={editorTextValue}
      />
      <LineDiffMarkers
        editor={editor}
        changedLines={removedLines}
        type="removed"
        editorTextValue={editorTextValue}
      />
      <EditorCommentButtons
        editorTextValue={editorTextValue}
        editor={editor}
        commentsByLineNumber={commentsByLineNumber}
        schemaScope={selectedSchemaInfo.id}
        addedModifiedLines={[...addedLines, ...modifiedLines]}
      />
      <StudioEditorContainer editor={editor} className="size-0 flex-1" />
    </>
  );
};
